import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faStickyNote } from "@fortawesome/pro-solid-svg-icons/faStickyNote";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { AnyEntityId, EntityTypeSupportingNotes } from "@mapmycustomers/shared/types/entity";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Modal, TextAreaWithMentions } from "@mapmycustomers/ui";

import { getAllTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./AddNoteModal.module.scss";
import EntityLabel from "./component/EntityLabel";
import { isCreating } from "./store";
import { createNote } from "./store/actions";

interface Props {
  allUsers: User[];
  analyticIssuer?: AnalyticsService;
  createNote: typeof createNote.request;
  creating: boolean;
  entityId: AnyEntityId;
  entityName: string;
  entityType: EntityTypeSupportingNotes;
  onHide: (added: boolean, note?: Note) => void;
  teams: Team[];
}

const AddNoteModal: React.FC<Props> = ({
  allUsers,
  analyticIssuer,
  createNote,
  creating,
  entityId,
  entityName,
  entityType,
  onHide,
  teams,
}) => {
  const intl = useIntl();

  const [note, setNote] = useState<string>("");
  const handleHideClick = useCallback(() => onHide(false), [onHide]);

  const handleCreateClick = useCallback(() => {
    createNote({
      callback: (note: Note) => {
        onHide(true, note);
        analyticIssuer?.clicked(["Note Created"], { entityId, entityType, note });
      },
      entityId,
      entityType,
      note,
    });
  }, [analyticIssuer, createNote, entityId, entityType, onHide, note]);

  return (
    <Modal
      className={styles.container}
      okButtonProps={{
        disabled: creating || !note,
        icon: <FontAwesomeIcon icon={faStickyNote} />,
        loading: creating,
      }}
      okText={intl.formatMessage({
        id: "addNoteModal.footer.createNote",
        defaultMessage: "Add Note",
        description: "Add button title on Add Note modal",
      })}
      onCancel={handleHideClick}
      onOk={handleCreateClick}
      open
      title={intl.formatMessage(
        {
          id: "addNoteModal.header",
          defaultMessage:
            "Add Note to {entityType, select, accounts {Company} contacts {Person} deals {Deal} other {Record}}",
          description: "Header of Add Note modal",
        },
        {
          entityType,
        }
      )}
    >
      <section className={styles.verticalSpace}>
        <EntityLabel entityName={entityName} entityType={entityType} />
        <TextAreaWithMentions
          allUsers={allUsers}
          hideDisabledUsers
          label={intl.formatMessage({
            id: "addNoteModal.field.note",
            defaultMessage: "Note",
            description: "Note field on the Add Note modal",
          })}
          maxLength={Number.MAX_SAFE_INTEGER}
          onChange={setNote}
          rows={4}
          teams={teams}
          value={note}
        />
      </section>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsersOfEntireOrganization(state),
  creating: isCreating(state),
  teams: getAllTeamsOfEntireOrganization(state),
});

const mapDispatchToProps = {
  createNote: createNote.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddNoteModal);
